<template>
  <div class="record-wrapper" v-loading.fullscreen.lock="loading">
    <!-- 游戏选择 -->
    <div class="record-header">
      <el-select v-model="currentGameId" placeholder="选择游戏" @change="onGameChange" filterable style="width: 300px;">
        <el-option v-for="g in allGames" :key="g.id" :label="g.title" :value="g.id" />
      </el-select>
    </div>

    <!-- 筛选区域 -->
    <div class="filter-section">
      <el-card shadow="hover">
        <div class="filter-content">
          <!-- 用户ID筛选 -->
          <div class="filter-item">
            <label class="filter-label">用户ID：</label>
            <el-input
              v-model="filterUserId"
              placeholder="请输入用户ID"
              clearable
              style="width: 200px;"
              @clear="handleFilter"
            />
          </div>

          <!-- 大章节筛选 -->
          <div class="filter-item">
            <label class="filter-label">大章节：</label>
            <el-select
              v-model="filterChapterId"
              placeholder="请选择大章节"
              clearable
              filterable
              style="width: 200px;"
              @change="handleChapterChange"
              @clear="handleFilter"
            >
              <el-option
                v-for="chapter in chapters"
                :key="chapter.id"
                :label="chapter.title"
                :value="chapter.id"
              />
            </el-select>
          </div>

          <!-- 节点筛选 -->
          <div class="filter-item">
            <label class="filter-label">节点：</label>
            <el-select
              v-model="filterNodeId"
              placeholder="请先选择大章节"
              clearable
              filterable
              :disabled="!filterChapterId"
              style="width: 250px;"
              @change="handleNodeChange"
              @clear="handleFilter"
            >
              <el-option
                v-for="node in nodes"
                :key="node.id"
                :label="`${node.title} (Order: ${node.order})`"
                :value="node.id"
              />
            </el-select>
          </div>

          <!-- 查询按钮 -->
          <el-button type="primary" icon="el-icon-search" @click="handleFilter">查询</el-button>
          <el-button icon="el-icon-refresh" @click="handleReset">重置</el-button>
        </div>
      </el-card>
    </div>

    <!-- 游戏记录表格 -->
    <div class="records-section">
      <el-table
        :data="records"
        style="width: 100%"
        @row-click="handleRowClick"
        :row-style="{ cursor: 'pointer' }"
        stripe
        border>
        <el-table-column prop="record_id" label="记录ID" width="100" />
        <el-table-column prop="user_id" label="用户ID" width="150" />
        <el-table-column prop="current_node_name" label="当前节点" min-width="150" />
        <el-table-column prop="current_chapter_name" label="当前章节" min-width="150" />
        <el-table-column prop="max_chapter_name" label="最大章节" min-width="150" />
        <el-table-column prop="create_time" label="创建时间" min-width="180">
          <template slot-scope="scope">
            {{ formatDate(scope.row.create_time) }}
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页组件 -->
      <div v-if="records.length > 0" style="text-align: center; margin-top: 20px;">
        <el-pagination
          @current-change="handleCurrentChange"
          @size-change="handleSizeChange"
          :current-page="currentPage"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="pageSize"
          layout="total, sizes, prev, pager, next, jumper"
          :total="totalCount">
        </el-pagination>
      </div>

      <!-- 空状态 -->
      <div v-if="!loading && records.length === 0" style="text-align: center; padding: 40px; color: #999;">
        <i class="el-icon-document" style="font-size: 64px; margin-bottom: 20px;"></i>
        <p>暂无游戏记录</p>
      </div>
    </div>

    <!-- 记录详情对话框 -->
    <el-dialog
      title="记录详情"
      :visible.sync="dialogVisible"
      width="80%"
      :before-close="handleClose"
      top="5vh"
    >
      <record-detail-content
        v-if="selectedRecordId"
        :record-id="selectedRecordId"
        :game="selectedGame"
      />
    </el-dialog>

    <footer style="margin-top: 60px; padding-top: 20px; border-top: 1px solid #eee; text-align: center; color: #999; font-size: 12px;">
      <div style="margin-bottom: 8px;">
        <a href="https://beian.miit.gov.cn" target="_blank" rel="noopener noreferrer" style="color: #999; text-decoration: none;">
         工业和信息化部备案管理系统网站 京ICP备2025135199号-1
        </a>
      </div>
      <div>主办单位：北京星域万象科技有限公司</div>
    </footer>
  </div>
</template>

<script lang="ts">
import {Component, Prop, Vue, Watch} from 'vue-property-decorator'
import service from '@/utils/request'
import RecordDetailContent from '@/components/RecordDetailContent.vue'
import {Game, getGames} from "@/api/game"

interface RecordChapter {
  id: number
  title: string
  order: number
}

interface RecordNode {
  id: number
  title: string
  order: number
}

interface RecordItem {
  record_id: number
  user_id: string
  current_node_name: string
  current_chapter_name: string
  max_chapter_name: string
  create_time: string
}

interface PaginationResponse {
  message: string
  data: RecordItem[]
  page: number
  page_size: number
  total_count: number
  total_pages: number
  has_next: boolean
  has_prev: boolean
}

@Component({
  components: { RecordDetailContent }
})
export default class RecordList extends Vue {
  @Prop({ required: true }) readonly game!: Game

  allGames: Game[] = []
  currentGameId: number | null = null
  records: RecordItem[] = []
  loading = false
  dialogVisible = false
  selectedRecordId: number | null = null
  currentPage = 1
  pageSize = 20
  totalCount = 0
  totalPages = 0
  hasNext = false
  hasPrev = false

  // 筛选相关
  filterUserId = ''
  filterChapterId: number | null = null
  filterNodeId: number | null = null
  filterNodeOrder: number | null = null
  chapters: RecordChapter[] = []
  nodes: RecordNode[] = []

  get selectedGame(): Game | null {
    if (this.currentGameId) {
      const found = this.allGames.find(g => g.id === this.currentGameId)
      if (found) return found
    }
    return this.game || null
  }

  @Watch("game", {immediate: true})
  async onGamePropChange(newGame: Game){
    if (newGame && newGame.id) {
      this.currentGameId = newGame.id
      await this.loadChaptersAndNodes()
      this.loadRecords()
    }
  }

  async mounted() {
    await this.loadAllGames()
  }

  async loadAllGames() {
    try {
      this.allGames = await getGames()
    } catch {
      // ignore
    }
  }

  onGameChange(gameId: number) {
    this.currentGameId = gameId
    // Reset filters when switching games
    this.filterChapterId = null
    this.filterNodeId = null
    this.filterNodeOrder = null
    this.nodes = []
    this.currentPage = 1
    this.loadChaptersAndNodes()
    this.loadRecords()
  }

  async loadChaptersAndNodes() {
    if (!this.currentGameId) return

    try {
      // 加载章节列表 - 使用新接口
      const response = await service.get('/api/record_log/chapters', {
        params: { game_id: this.currentGameId }
      })
      if (response.data.message === 'success') {
        this.chapters = response.data.data
      }
    } catch (error) {
      console.error('加载章节失败:', error)
    }
  }

  async loadChapterNodes(chapterId: number) {
    if (!this.currentGameId) return

    try {
      // 加载指定章节的节点列表 - 使用新接口
      const response = await service.get(`/api/record_log/chapters/${chapterId}/nodes`, {
        params: { game_id: this.currentGameId }
      })
      if (response.data.message === 'success') {
        this.nodes = response.data.data
      }
    } catch (error) {
      console.error('加载节点失败:', error)
      this.$message.error('加载节点列表失败')
    }
  }

  async loadRecords() {
    if (!this.currentGameId) {
      console.error('Game对象未定义或缺少id')
      return
    }

    this.loading = true
    try {
      // 构建查询参数
      const params: any = {
        game_id: this.currentGameId,
        page: this.currentPage,
        page_size: this.pageSize
      }

      // 添加用户ID筛选
      if (this.filterUserId) {
        params.user_id = parseInt(this.filterUserId)
      }

      // 添加节点order筛选（查询最大节点order >= 该值的记录）
      if (this.filterNodeOrder !== null) {
        params.min_order = this.filterNodeOrder
      }

      const response = await service.get('/api/record_log', { params })
      if (response.data.message === 'success') {
        this.records = response.data.data
        const pagination = response.data.pagination
        this.currentPage = pagination.page
        this.pageSize = pagination.page_size
        this.totalCount = pagination.total_count
        this.totalPages = pagination.total_pages
        this.hasNext = pagination.has_next
        this.hasPrev = pagination.has_prev
      }
    } catch (error) {
      console.error('获取记录失败:', error)
      this.$message.error('获取记录失败')
    } finally {
      this.loading = false
    }
  }

  handleRowClick(row: RecordItem) {
    this.selectedRecordId = row.record_id
    this.dialogVisible = true
  }

  handleClose() {
    this.dialogVisible = false
    this.selectedRecordId = null
  }

  formatDate(dateString: string) {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleString('zh-CN')
  }

  handleCurrentChange(page: number) {
    this.currentPage = page
    this.loadRecords()
  }

  handleSizeChange(size: number) {
    this.pageSize = size
    this.currentPage = 1
    this.loadRecords()
  }

  // 筛选相关方法
  handleFilter() {
    this.currentPage = 1
    this.loadRecords()
  }

  handleReset() {
    this.filterUserId = ''
    this.filterChapterId = null
    this.filterNodeId = null
    this.filterNodeOrder = null
    this.nodes = []
    this.currentPage = 1
    this.loadRecords()
  }

  async handleChapterChange(chapterId: number | null) {
    // 切换章节时清空节点选择
    this.filterNodeId = null
    this.filterNodeOrder = null
    this.nodes = []

    // 如果选择了章节，加载该章节的节点
    if (chapterId) {
      await this.loadChapterNodes(chapterId)
    }
  }

  handleNodeChange(nodeId: number | null) {
    // 选择节点时，保存节点的order值
    if (nodeId) {
      const selectedNode = this.nodes.find(n => n.id === nodeId)
      if (selectedNode) {
        this.filterNodeOrder = selectedNode.order
      }
    } else {
      this.filterNodeOrder = null
    }
  }
}
</script>

<style scoped>
.record-wrapper {
  padding: 20px;
}

.record-header {
  margin-bottom: 16px;
}

/* 筛选区域 */
.filter-section {
  margin-bottom: 20px;
}

.filter-content {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.filter-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.filter-label {
  font-size: 14px;
  color: #606266;
  white-space: nowrap;
}

.game-info {
  text-align: center;
  margin-bottom: 40px;
}

.game-info h1 {
  font-size: 32px;
  color: #409EFF;
  margin-bottom: 16px;
}

.game-info p {
  font-size: 16px;
  color: #666;
  line-height: 1.6;
}

.nav-section {
  text-align: center;
  margin-bottom: 40px;
}

.records-section h2 {
  color: #303133;
  margin-bottom: 20px;
}

.el-table {
  margin-top: 20px;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .record-wrapper {
    padding: 10px;
  }

  .game-info h1 {
    font-size: 24px;
  }

  .game-info p {
    font-size: 14px;
  }

  .filter-content {
    flex-direction: column;
    align-items: stretch;
  }

  .filter-item {
    width: 100%;
  }
}
</style>